import { formatCurrency, InvoiceSplitRuleType } from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { DeleteButton } from "components/ui/action-button";
import { Checkbox } from "components/ui/checkbox";
import { Input } from "components/ui/input";
import { SecondaryButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import React from "react";
import { useFeature } from "store/selectors/hooks";
import { computeSplitDistribution, SplitRuleDraft } from "./invoice-split-math";
import { percentStringToBasisPoints } from "./rate-percent";

export type { SplitRuleDraft } from "./invoice-split-math";

interface ApiSplitRule {
    type: InvoiceSplitRuleType;
    recipientTerritoryId: string;
    rateBasisPoints: number;
    enabled: boolean;
    notes: string | null;
}

export const emptySplitRuleDraft = (): SplitRuleDraft => ({
    type: InvoiceSplitRuleType.TerritorySplit,
    recipientTerritoryId: "",
    ratePercent: "30",
    enabled: true,
    notes: "",
});

export const apiRuleToDraft = (rule: {
    type: InvoiceSplitRuleType;
    recipientTerritoryId: string;
    rateBasisPoints: number;
    enabled: boolean;
    notes: string | null;
}): SplitRuleDraft => ({
    type: rule.type,
    recipientTerritoryId: rule.recipientTerritoryId,
    ratePercent: (rule.rateBasisPoints / 100).toString(),
    enabled: rule.enabled,
    notes: rule.notes ?? "",
});

export const splitRuleDraftsToApi = (drafts: SplitRuleDraft[]): ApiSplitRule[] =>
    drafts
        .filter(draft => draft.recipientTerritoryId !== "")
        .map(draft => ({
            type: draft.type,
            recipientTerritoryId: draft.recipientTerritoryId,
            rateBasisPoints: percentStringToBasisPoints(draft.ratePercent),
            enabled: draft.enabled,
            notes: draft.notes.trim() === "" ? null : draft.notes,
        }));

const formatCents = (cents: number): string => formatCurrency(cents / 100);

const formatPercent = (percent: number): string =>
    `${Number.isInteger(percent) ? percent : percent.toFixed(2)}%`;

const typeOptions = [
    { value: InvoiceSplitRuleType.TerritorySplit, text: "Territory" },
    { value: InvoiceSplitRuleType.ReferralSplit, text: "Referral" },
];

interface Props {
    rules: SplitRuleDraft[];
    totalCents: number;
    onChange: (rules: SplitRuleDraft[]) => void;
}

export const InvoiceSplitRulesEditor: React.FC<Props> = ({
    rules,
    totalCents,
    onChange,
}) => {
    const featureEnabled = useFeature("franchisePayments");
    const trpcClient = trpc.useTRPC();
    const territoriesQuery = useQuery({
        ...trpcClient.franchiseTerritory.listForSplitRecipients.queryOptions(),
        enabled: featureEnabled,
    });

    if (!featureEnabled) {
        return null;
    }

    const territoryOptions = (territoriesQuery.data ?? [])
        .filter(territory => territory.assignedRegionId !== null)
        .map(territory => ({
            value: territory.id,
            text: `${territory.name} (${territory.code})`,
        }));

    const labelByTerritoryId = new Map(
        territoryOptions.map(option => [option.value, option.text]),
    );

    const distribution = computeSplitDistribution(rules, totalCents, labelByTerritoryId);
    const showDistribution = totalCents > 0 && distribution.allocations.length > 0;

    const updateRule = (index: number, patch: Partial<SplitRuleDraft>) =>
        onChange(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));

    const addRule = () => onChange([...rules, emptySplitRuleDraft()]);

    const removeRule = (index: number) => onChange(rules.filter((_, i) => i !== index));

    return (
        <div className="space-y-4">
            <div>
                <h3 className="font-semibold">Splits</h3>
                <p className="text-sm text-gray-500">
                    By default all revenue stays with this office. Add a split to route a
                    share to another franchise territory.
                </p>
            </div>

            {rules.length === 0 ? null : (
                <div className="space-y-3">
                    {rules.map((rule, index) => (
                        <div
                            key={index}
                            className="flex items-end gap-3 rounded-lg bg-gray-50 p-3">
                            <div className="flex flex-1 flex-wrap items-end gap-3">
                                <Select
                                    label="Type"
                                    value={rule.type}
                                    onChange={e =>
                                        updateRule(index, {
                                            type: e.target.value as InvoiceSplitRuleType,
                                        })
                                    }>
                                    {typeOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.text}
                                        </option>
                                    ))}
                                </Select>
                                <div className="flex-1 min-w-48">
                                    <Select
                                        label="Recipient"
                                        value={rule.recipientTerritoryId}
                                        onChange={e =>
                                            updateRule(index, {
                                                recipientTerritoryId: e.target.value,
                                            })
                                        }>
                                        <option value="">(select recipient)</option>
                                        {territoryOptions.map(option => (
                                            <option
                                                key={option.value}
                                                value={option.value}>
                                                {option.text}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="w-24">
                                    <Input
                                        label="Split %"
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={rule.ratePercent}
                                        onChange={e =>
                                            updateRule(index, {
                                                ratePercent: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                                <div className="flex-1 min-w-40">
                                    <Input
                                        label="Note"
                                        value={rule.notes}
                                        onChange={e =>
                                            updateRule(index, { notes: e.target.value })
                                        }
                                    />
                                </div>
                                <Checkbox
                                    label="Active"
                                    checked={rule.enabled}
                                    onChange={e =>
                                        updateRule(index, { enabled: e.target.checked })
                                    }
                                />
                            </div>
                            <DeleteButton
                                backgroundColor="bg-white"
                                size="sm"
                                onClick={() => removeRule(index)}
                            />
                        </div>
                    ))}
                </div>
            )}

            {territoriesQuery.isSuccess && territoryOptions.length === 0 ? (
                <p className="text-sm text-gray-500">
                    No franchise territories with an assigned region are available to
                    receive a split.
                </p>
            ) : null}

            <SecondaryButton type="button" onClick={addRule}>
                <span className="text-sm">Add split</span>
            </SecondaryButton>

            {showDistribution ? (
                <div className="rounded-lg border border-gray-200 p-3 text-sm">
                    <div className="mb-2 font-semibold">
                        Distribution on full payment ({formatCents(totalCents)})
                    </div>
                    <table className="w-full">
                        <tbody>
                            {distribution.allocations.map((allocation, index) => (
                                <tr key={index}>
                                    <td className="py-1">{allocation.label}</td>
                                    <td className="py-1 text-right text-gray-500">
                                        {formatPercent(allocation.percent)}
                                    </td>
                                    <td className="py-1 text-right font-medium">
                                        {formatCents(allocation.cents)}
                                    </td>
                                </tr>
                            ))}
                            <tr className="border-t border-gray-200">
                                <td className="py-1">This office</td>
                                <td className="py-1 text-right text-gray-500">
                                    {formatPercent(distribution.officePercent)}
                                </td>
                                <td className="py-1 text-right font-medium">
                                    {formatCents(distribution.officeCents)}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    {distribution.overAllocated ? (
                        <p className="mt-2 text-red-600">
                            Splits add up to{" "}
                            {formatPercent(distribution.totalSplitPercent)}, which is more
                            than the invoice total.
                        </p>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};
